<script setup lang="ts">
import { ref, computed } from 'vue'
import { Roles } from '@/constants.ts'
import { jwtDecode } from 'jwt-decode'
import jwtService from '@/services/jwtService'
import agentsHttpService from '@/services/http/agentsHttpService'
import type { AgentRequestModel, AgentBankDetailsModel } from '@/services/http/httpResponses'
import { AgentRequestStatus } from '@/services/http/httpResponses'
import { z } from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import type { JwtPayload } from '@/types/auth'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Agent],
  },
})

const requests = ref<AgentRequestModel[]>([])
const isAgent = computed(() => {
  const token = jwtService.getToken()
  if (token) {
    const decoded = jwtDecode<JwtPayload>(token)
    return decoded.role === Roles.Agent
  }
  return false
})

const showBankDetailsDialog = ref(false)
const bankDetails = ref<AgentBankDetailsModel>({
  firstName: '',
  lastName: '',
  middleName: null,
  iin: '',
  bankAccountNumber: '',
  bankCode: '',
})
const createFlowAwaitingBankDetails = ref(false)
const showCreateRequestDialog = ref(false)
const requestAmount = ref<number | null>(null)
const availableAmount = ref<number>(0)

const bankDetailsSchema = z.object({
  firstName: z.string().min(1, { message: 'Укажите имя' }),
  lastName: z.string().min(1, { message: 'Укажите фамилию' }),
  middleName: z.string().nullable().optional(),
  iin: z.string().length(12, { message: 'ИИН должен содержать ровно 12 цифр' }),
  bankAccountNumber: z.string().min(1, { message: 'Укажите номер банковского счета' }),
  bankCode: z.string().min(1, { message: 'Укажите код банка' }),
})

const bankDetailsResolver = zodResolver(bankDetailsSchema)
const bankDetailsInitialValues = computed(() => ({
  firstName: bankDetails.value.firstName,
  lastName: bankDetails.value.lastName,
  middleName: bankDetails.value.middleName,
  iin: bankDetails.value.iin,
  bankAccountNumber: bankDetails.value.bankAccountNumber,
  bankCode: bankDetails.value.bankCode,
}))

const requestAmountSchema = z.object({
  requestAmount: z
    .number({ message: 'Укажите сумму' })
    .min(1, { message: 'Сумма должна быть больше 0' })
    .refine(() => availableAmount.value > 0, {
      message: 'Доступная сумма равна 0, вывод невозможен',
    }),
})

const requestAmountResolver = zodResolver(requestAmountSchema)
const requestAmountInitialValues = computed(() => ({
  requestAmount: requestAmount.value,
}))

const refreshRequests = async () => {
  if (isAgent.value) {
    const response = await agentsHttpService.getAgentRequests()
    requests.value = response.requests
    const availableAmountResponse = await agentsHttpService.getAgentAvailableAmount()
    availableAmount.value = availableAmountResponse.availableAmount
  } else {
    const response = await agentsHttpService.getAllAgentRequests()
    requests.value = response.requests
  }
}

const getStatus = (status: AgentRequestStatus) => {
  return status === AgentRequestStatus.New ? 'Новая' : 'Выполнена'
}

const toggleStatus = async (row: AgentRequestModel) => {
  const status =
    row.status === AgentRequestStatus.New ? AgentRequestStatus.Completed : AgentRequestStatus.New
  await agentsHttpService.updateAgentRequestStatus(row.id, status)
  row.status = status
}

const openBankDetailsDialog = async () => {
  const response = await agentsHttpService.getAgentBankDetails()
  if (response.bankDetails) {
    bankDetails.value = response.bankDetails
  } else {
    bankDetails.value = {
      firstName: '',
      lastName: '',
      middleName: null,
      iin: '',
      bankAccountNumber: '',
      bankCode: '',
    }
  }
  showBankDetailsDialog.value = true
}

const saveBankDetails = async ({
  valid,
  values,
}: {
  valid: boolean
  values: AgentBankDetailsModel
}) => {
  if (!valid) {
    return
  }
  bankDetails.value = values
  const response = await agentsHttpService.getAgentBankDetails()
  if (response.bankDetails) {
    await agentsHttpService.updateAgentBankDetails(bankDetails.value)
  } else {
    await agentsHttpService.createAgentBankDetails(bankDetails.value)
  }
  await refreshRequests()
  showBankDetailsDialog.value = false
  if (createFlowAwaitingBankDetails.value) {
    const av = await agentsHttpService.getAgentAvailableAmount()
    availableAmount.value = av.availableAmount
    requestAmount.value = null
    showCreateRequestDialog.value = true
    createFlowAwaitingBankDetails.value = false
  }
}

const openCreateRequestDialog = async () => {
  const response = await agentsHttpService.getAgentBankDetails()
  if (!response.bankDetails) {
    createFlowAwaitingBankDetails.value = true
    showBankDetailsDialog.value = true
    return
  }
  const amountResponse = await agentsHttpService.getAgentAvailableAmount()
  availableAmount.value = amountResponse.availableAmount
  requestAmount.value = null
  showCreateRequestDialog.value = true
}

const createRequest = async ({
  valid,
  values,
}: {
  valid: boolean
  values: { requestAmount: number | null }
}) => {
  if (!valid || values.requestAmount === null) {
    return
  }
  await agentsHttpService.createAgentRequest(values.requestAmount)
  await refreshRequests()
  showCreateRequestDialog.value = false
}

await refreshRequests()
</script>
<template>
  <div class="flex flex-col gap-4 w-full">
    <div class="flex justify-end gap-2">
      <Button v-if="isAgent" label="Реквизиты агента" @click="openBankDetailsDialog" />
      <Button v-if="isAgent" label="Создать заявку" @click="openCreateRequestDialog" />
    </div>

    <DataTable ref="dt" :value="requests" dataKey="id">
      <Column header="Статус заявки" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getStatus(slotProps.data.status) }}
        </template>
      </Column>
      <Column header="Дата создания" style="min-width: 12rem">
        <template #body="slotProps">
          {{ slotProps.data.createdAt.toLocaleString('ru-RU') }}
        </template>
      </Column>
      <Column header="Сумма" style="min-width: 12rem">
        <template #body="slotProps">
          {{ `${slotProps.data.amount} KZT` }}
        </template>
      </Column>
      <Column header="Банковские реквизиты" style="min-width: 12rem">
        <template #body="slotProps">
          <p>
            {{
              `${slotProps.data.bankDetails.firstName} ${slotProps.data.bankDetails.lastName} ${slotProps.data.bankDetails.middleName ?? ''}`
            }}
          </p>
          <p>{{ slotProps.data.bankDetails.iin }}</p>
          <p>{{ slotProps.data.bankDetails.bankAccountNumber }}</p>
          <p>{{ slotProps.data.bankDetails.bankCode }}</p>
        </template>
      </Column>
      <Column v-if="!isAgent" header="Агент" style="min-width: 12rem">
        <template #body="slotProps">
          <p>{{ slotProps.data.agent?.name }}</p>
          <p v-if="slotProps.data.agent?.email">{{ slotProps.data.agent.email }}</p>
        </template>
      </Column>
      <Column v-if="!isAgent" header="Действия" style="min-width: 12rem">
        <template #body="slotProps">
          <Button
            :label="slotProps.data.status === AgentRequestStatus.New ? 'Исполнить' : 'Отменить'"
            :severity="
              slotProps.data.status === AgentRequestStatus.Completed ? 'secondary' : undefined
            "
            @click="toggleStatus(slotProps.data)"
          />
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showBankDetailsDialog"
      header="Реквизиты агента"
      :modal="true"
      :class="'w-[50vw]'"
      :closable="false"
    >
      <Form
        v-slot="$form"
        :resolver="bankDetailsResolver"
        :initialValues="bankDetailsInitialValues"
        @submit="saveBankDetails"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="firstName">Имя</label>
          <InputText id="firstName" name="firstName" class="w-full" />
          <Message v-if="$form.firstName?.invalid" severity="error" size="small" variant="simple">
            {{ $form.firstName.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="lastName">Фамилия</label>
          <InputText id="lastName" name="lastName" class="w-full" />
          <Message v-if="$form.lastName?.invalid" severity="error" size="small" variant="simple">
            {{ $form.lastName.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="middleName">Отчество (опционально)</label>
          <InputText id="middleName" name="middleName" class="w-full" />
          <Message v-if="$form.middleName?.invalid" severity="error" size="small" variant="simple">
            {{ $form.middleName.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="iin">ИИН</label>
          <InputText id="iin" name="iin" class="w-full" />
          <Message v-if="$form.iin?.invalid" severity="error" size="small" variant="simple">
            {{ $form.iin.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="bankAccountNumber">Номер банковского счета</label>
          <InputText id="bankAccountNumber" name="bankAccountNumber" class="w-full" />
          <Message
            v-if="$form.bankAccountNumber?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.bankAccountNumber.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="bankCode">Код банка</label>
          <InputText id="bankCode" name="bankCode" class="w-full" />
          <Message v-if="$form.bankCode?.invalid" severity="error" size="small" variant="simple">
            {{ $form.bankCode.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showBankDetailsDialog = false" />
          <Button class="w-full" type="submit" label="Сохранить" />
        </div>
      </Form>
    </Dialog>

    <Dialog
      v-model:visible="showCreateRequestDialog"
      header="Создать заявку"
      :modal="true"
      :class="'w-[30vw]'"
      :closable="false"
    >
      <Form
        v-slot="$form"
        :resolver="requestAmountResolver"
        :initialValues="requestAmountInitialValues"
        @submit="createRequest"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <p>Доступная сумма: {{ `${availableAmount} KZT` }}</p>
          <label for="requestAmount">Сумма для вывода</label>
          <InputNumber
            id="requestAmount"
            name="requestAmount"
            :min="0"
            :max="availableAmount"
            mode="decimal"
            :maxFractionDigits="2"
            :useGrouping="false"
            :inputMode="'decimal'"
            :locale="'en-US'"
          />
          <Message
            v-if="$form.requestAmount?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.requestAmount.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showCreateRequestDialog = false" />
          <Button class="w-full" type="submit" label="Создать" />
        </div>
      </Form>
    </Dialog>
  </div>
</template>

<style scoped></style>
